import { useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';

// Custom event name for localStorage sync
const STORAGE_EVENT = 'local-storage-sync';

export function useLocalStorage(key, initialValue) {
  // Get stored value or use initial value
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      logger.warn(`Error reading localStorage key "${key}"`, { error: error.message });
      return initialValue;
    }
  });

  // Listen for changes from other components
  useEffect(() => {
    const handleStorageSync = (event) => {
      if (event.detail?.key === key) {
        setStoredValue(event.detail.value);
      }
    };

    window.addEventListener(STORAGE_EVENT, handleStorageSync);
    return () => window.removeEventListener(STORAGE_EVENT, handleStorageSync);
  }, [key]);

  // Update localStorage when value changes
  const setValue = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
      
      // Dispatch custom event to sync other components
      window.dispatchEvent(new CustomEvent(STORAGE_EVENT, {
        detail: { key, value: valueToStore }
      }));
    } catch (error) {
      logger.warn(`Error setting localStorage key "${key}"`, { error: error.message });
    }
  }, [key, storedValue]);

  // Remove from localStorage
  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
      
      // Dispatch custom event to sync other components
      window.dispatchEvent(new CustomEvent(STORAGE_EVENT, {
        detail: { key, value: initialValue }
      }));
    } catch (error) {
      logger.warn(`Error removing localStorage key "${key}"`, { error: error.message });
    }
  }, [key, initialValue]);

  return [storedValue, setValue, removeValue];
}

export default useLocalStorage;
